import { PIEVE_TRIGGER_POLL_INTERVAL_MINUTES_MIN, normalizePieveTriggerPollIntervalMinutes } from '@shared/utils';
import normalizeText from '@/lib/normalizeWhitespaceText.js';

const EMPTY_ARRAY = [];

export const normalizeTriggerType = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  if (!normalized) {
    return 'app_event';
  }

  if (normalized === 'appevent') {
    return 'app_event';
  }

  if (normalized === 'longpoll' || normalized === 'polling') {
    return 'long_poll';
  }

  if (normalized === 'app_event' || normalized === 'long_poll') {
    return normalized;
  }

  return 'app_event';
};

const getDateTimestamp = (value) => {
  const timestamp = new Date(value).getTime();

  if (!Number.isFinite(timestamp)) {
    return 0;
  }

  return timestamp;
};

const getSafeRevision = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue < 1) {
    return 1;
  }

  return Math.floor(numericValue);
};

export const deriveTriggerSummary = ({ triggerType, appId, actionId, pollIntervalMinutes }) => {
  const safeAppId = normalizeText(appId) || 'app';
  const safeActionId = normalizeText(actionId);

  if (triggerType === 'long_poll') {
    const safeInterval = normalizePieveTriggerPollIntervalMinutes(pollIntervalMinutes, { min: 1 }) || PIEVE_TRIGGER_POLL_INTERVAL_MINUTES_MIN;
    return `Poll ${safeAppId}${safeActionId ? `:${safeActionId}` : ''} every ${safeInterval}m`;
  }

  return `On ${safeAppId}${safeActionId ? `:${safeActionId}` : ''} event`;
};

export const mapApiTriggerToLocal = (apiTrigger) => {
  const persistedTriggerId = normalizeText(apiTrigger?.id || apiTrigger?.triggerId);

  if (!persistedTriggerId) {
    return null;
  }

  const details = apiTrigger?.details && typeof apiTrigger.details === 'object' && !Array.isArray(apiTrigger.details) ? apiTrigger.details : {};
  const triggerConfig = apiTrigger?.triggerConfig && typeof apiTrigger.triggerConfig === 'object' && !Array.isArray(apiTrigger.triggerConfig) ? apiTrigger.triggerConfig : details;
  const threadId = normalizeText(apiTrigger?.threadId);
  const triggerType = normalizeTriggerType(apiTrigger?.triggerType || triggerConfig?.triggerType || 'app_event');
  const appId = normalizeText(apiTrigger?.appId || triggerConfig?.appId);
  const actionId = normalizeText(apiTrigger?.actionId || triggerConfig?.actionId);
  const externalCompanyId = normalizeText(apiTrigger?.externalCompanyId || triggerConfig?.externalCompanyId);
  const credentialId = normalizeText(apiTrigger?.credentialId || triggerConfig?.credentialId);
  const pollIntervalMinutes = normalizePieveTriggerPollIntervalMinutes(apiTrigger?.pollIntervalMinutes || triggerConfig?.pollIntervalMinutes, { min: 1 });
  const pollArguments = triggerConfig?.pollArguments && typeof triggerConfig.pollArguments === 'object' && !Array.isArray(triggerConfig.pollArguments) ? triggerConfig.pollArguments : {};
  const pollOptions = triggerConfig?.pollOptions && typeof triggerConfig.pollOptions === 'object' && !Array.isArray(triggerConfig.pollOptions) ? triggerConfig.pollOptions : {};
  const appVersion = normalizeText(triggerConfig?.appVersion) || 'latest';
  const maxEventsPerPollRaw = Number(triggerConfig?.maxEventsPerPoll);
  const maxEventsPerPoll = Number.isInteger(maxEventsPerPollRaw) && maxEventsPerPollRaw > 0 ? maxEventsPerPollRaw : null;
  const title = normalizeText(apiTrigger?.title) || 'Untitled trigger';
  const prompt = String(apiTrigger?.prompt || '').trim();
  return {
    id: persistedTriggerId,
    threadId: threadId || null,
    workflowStepId: normalizeText(apiTrigger?.workflowStepId) || null,
    title,
    prompt,
    active: Boolean(apiTrigger?.active),
    pinned: false,
    pinnedAt: null,
    triggerType,
    triggerSummary:
      normalizeText(apiTrigger?.triggerSummary) ||
      deriveTriggerSummary({
        triggerType,
        appId,
        actionId,
        pollIntervalMinutes
      }),
    appId: appId || null,
    actionId: actionId || null,
    externalCompanyId: externalCompanyId || null,
    credentialId: credentialId || null,
    pollIntervalMinutes,
    lastPolledAt: apiTrigger?.lastPolledAt || null,
    lastEventAt: apiTrigger?.lastEventAt || null,
    triggerConfig: {
      triggerType,
      appId: appId || null,
      actionId: actionId || null,
      externalCompanyId: externalCompanyId || null,
      credentialId: credentialId || null,
      appVersion,
      pollIntervalMinutes,
      pollArguments,
      pollOptions,
      maxEventsPerPoll
    },
    promptSummary: normalizeText(apiTrigger?.promptSummary || triggerConfig?.promptSummary) || null,
    actionLabel: normalizeText(apiTrigger?.actionLabel || triggerConfig?.actionLabel) || null,
    composerModel: normalizeText(apiTrigger?.composerModel || triggerConfig?.composerModel) || null,
    composerReasoningEffort: normalizeText(apiTrigger?.composerReasoningEffort || triggerConfig?.composerReasoningEffort) || null,
    revision: getSafeRevision(apiTrigger?.revision),
    createdAt: apiTrigger?.createdAt || null,
    updatedAt: apiTrigger?.updatedAt || null,
    archivedAt: apiTrigger?.archivedAt || null,
    latestRunThread: apiTrigger?.latestRunThread || null
  };
};

export const getTriggerItemsFromListResult = (listResult) => {
  const sourceItems = Array.isArray(listResult?.items) ? listResult.items : Array.isArray(listResult?.triggers) ? listResult.triggers : EMPTY_ARRAY;

  if (sourceItems.length < 1) {
    return EMPTY_ARRAY;
  }

  const mappedItems = sourceItems.map((item) => mapApiTriggerToLocal(item)).filter(Boolean);
  return mappedItems.length > 0 ? mappedItems : EMPTY_ARRAY;
};

export const getTriggerItemFromResult = (triggerResult) => {
  const rawTrigger = triggerResult?.trigger && typeof triggerResult.trigger === 'object' && !Array.isArray(triggerResult.trigger) ? triggerResult.trigger : triggerResult && typeof triggerResult === 'object' && !Array.isArray(triggerResult) ? triggerResult : null;

  return mapApiTriggerToLocal(rawTrigger);
};

export const sortTriggers = (triggers = []) => {
  if (!Array.isArray(triggers) || triggers.length < 1) {
    return EMPTY_ARRAY;
  }

  return [...triggers].sort((left, right) => {
    const leftPinnedAt = getDateTimestamp(left?.pinnedAt);
    const rightPinnedAt = getDateTimestamp(right?.pinnedAt);

    if (leftPinnedAt !== rightPinnedAt) {
      return rightPinnedAt - leftPinnedAt;
    }

    const leftUpdatedAt = Math.max(getDateTimestamp(left?.updatedAt), getDateTimestamp(left?.createdAt));
    const rightUpdatedAt = Math.max(getDateTimestamp(right?.updatedAt), getDateTimestamp(right?.createdAt));

    if (leftUpdatedAt !== rightUpdatedAt) {
      return rightUpdatedAt - leftUpdatedAt;
    }

    return String(right?.id || '').localeCompare(String(left?.id || ''));
  });
};

export const findTriggerIndex = ({ list = [], triggerId }) => {
  const normalizedTriggerId = normalizeText(triggerId);

  if (!normalizedTriggerId) {
    return -1;
  }

  return list.findIndex((trigger) => normalizeText(trigger?.id) === normalizedTriggerId);
};

export const findTriggerByRequestedId = ({ triggers = [], requestedId }) => {
  const normalizedRequestedId = normalizeText(requestedId);

  if (!normalizedRequestedId) {
    return null;
  }

  return (Array.isArray(triggers) ? triggers : []).find((trigger) => normalizeText(trigger?.id) === normalizedRequestedId) || null;
};

export const buildTriggerConfigForApi = (trigger) => {
  const config = trigger?.triggerConfig && typeof trigger.triggerConfig === 'object' && !Array.isArray(trigger.triggerConfig) ? trigger.triggerConfig : {};

  return {
    triggerType: normalizeTriggerType(trigger?.triggerType || config?.triggerType || 'app_event'),
    appId: normalizeText(trigger?.appId || config?.appId) || null,
    actionId: normalizeText(trigger?.actionId || config?.actionId) || null,
    externalCompanyId: normalizeText(trigger?.externalCompanyId || config?.externalCompanyId) || null,
    credentialId: normalizeText(trigger?.credentialId || config?.credentialId) || null,
    appVersion: normalizeText(config?.appVersion) || 'latest',
    pollIntervalMinutes: normalizePieveTriggerPollIntervalMinutes(trigger?.pollIntervalMinutes || config?.pollIntervalMinutes, { min: 1 }),
    pollArguments: config?.pollArguments && typeof config.pollArguments === 'object' && !Array.isArray(config.pollArguments) ? config.pollArguments : {},
    pollOptions: config?.pollOptions && typeof config.pollOptions === 'object' && !Array.isArray(config.pollOptions) ? config.pollOptions : {},
    maxEventsPerPoll: Number.isInteger(Number(config?.maxEventsPerPoll)) ? Number(config?.maxEventsPerPoll) : null
  };
};

export const normalizeTriggerConfigPatch = (patch = {}) => {
  const sourcePatch = patch && typeof patch === 'object' && !Array.isArray(patch) ? patch : {};
  const nextPatch = {};

  if (Object.prototype.hasOwnProperty.call(sourcePatch, 'triggerType')) {
    nextPatch.triggerType = normalizeTriggerType(sourcePatch.triggerType);
  }

  const stringFields = ['appId', 'actionId', 'externalCompanyId', 'credentialId', 'appVersion'];

  for (const field of stringFields) {
    if (!Object.prototype.hasOwnProperty.call(sourcePatch, field)) {
      continue;
    }

    const normalizedValue = normalizeText(sourcePatch[field]);
    nextPatch[field] = normalizedValue || null;
  }

  if (Object.prototype.hasOwnProperty.call(sourcePatch, 'pollIntervalMinutes')) {
    nextPatch.pollIntervalMinutes = normalizePieveTriggerPollIntervalMinutes(sourcePatch.pollIntervalMinutes, { min: 1 });
  }

  if (Object.prototype.hasOwnProperty.call(sourcePatch, 'pollArguments')) {
    nextPatch.pollArguments = sourcePatch.pollArguments && typeof sourcePatch.pollArguments === 'object' && !Array.isArray(sourcePatch.pollArguments) ? sourcePatch.pollArguments : {};
  }

  if (Object.prototype.hasOwnProperty.call(sourcePatch, 'pollOptions')) {
    nextPatch.pollOptions = sourcePatch.pollOptions && typeof sourcePatch.pollOptions === 'object' && !Array.isArray(sourcePatch.pollOptions) ? sourcePatch.pollOptions : {};
  }

  if (Object.prototype.hasOwnProperty.call(sourcePatch, 'maxEventsPerPoll')) {
    const maxEventsPerPoll = Number(sourcePatch.maxEventsPerPoll);
    nextPatch.maxEventsPerPoll = Number.isInteger(maxEventsPerPoll) && maxEventsPerPoll > 0 ? maxEventsPerPoll : null;
  }

  return nextPatch;
};
